/**
 * Thinking timings live outside the component tree because they have to survive one.
 * `MessageList` renders the in-flight turn through `StreamingMessage` and hands it to
 * `StableMessageList` the moment the run finishes, so `Thinking` unmounts and remounts with
 * `isComplete` already true — a ref inside the component loses the clock at exactly the moment
 * the duration is needed, and the header reports the floor instead. Keyed by message id, so the
 * remounted instance reads back the measurement its predecessor started.
 */
const timings = new Map<string, { startedAt: number; seconds?: number }>();

/** First sighting of an unfinished turn starts its clock. Later sightings must not restart it. */
export function startThinking(messageId: string) {
  if (!messageId || timings.has(messageId)) {
    return;
  }

  timings.set(messageId, { startedAt: Date.now() });
}

/**
 * Elapsed seconds, frozen on first read so a re-render cannot inflate it. Returns 0 for a turn
 * this session never saw run — a message loaded from history carries no measurement at all.
 */
export function finishThinking(messageId: string): number {
  const timing = timings.get(messageId);

  if (!timing) {
    return 0;
  }

  if (timing.seconds === undefined) {
    timing.seconds = Math.round((Date.now() - timing.startedAt) / 1000);
  }

  return timing.seconds;
}
